<script lang="ts">
  import { onDestroy, onMount } from "svelte"
  import { mdiSelectionEllipseRemove } from "@mdi/js"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import type { Snippet } from "svelte"
  import { createPropertyCustomer } from "../../../../../../../../../../../../../../api/kehrblatt/property-customer-registry/property-customers"
  import { fetchProperties } from "../../../../../../../../../../../../../../api/kehrblatt/property-registry/properties"
  import PropertyUnitAsyncMultiSelect from "../../../../../../../../../../_shared/_modules/property-unit-async-multi-select"
  import {
    defaultOutreachEnabledForRole,
    normalizePropertyCustomerRole,
    propertyCustomerRoleOptions,
  } from "../../../../../../../../../../../../_shared/_logic/customer-roles"

  type OverlayHostProps = {
    onremoveOverlay?: () => void
    onshowOverlay?: () => void
    setActionButtonSnippet?: (snippet: Snippet | null) => void
  }

  type Props = {
    organizationId: string
    customerId: string
    properties?: Property[]
    propertyLinks?: PropertyCustomer[]
    onLinked?: (payload: { links: PropertyCustomer[]; properties: Property[] }) => void
  }

  let {
    organizationId,
    customerId,
    properties = [],
    propertyLinks = [],
    onLinked = () => {},
    onremoveOverlay = null,
    onshowOverlay = null,
    setActionButtonSnippet = null,
  }: Props & OverlayHostProps = $props()

  let selectedUnits = $state<string[]>([])
  let role = $state<PropertyCustomerRole>("OTHER")
  let submitting = $state(false)

  const buildOptionValue = (propertyId: string, unitId?: string | null) =>
    `${propertyId}::${unitId ?? ""}`

  const linkedTargets = $derived(
    (propertyLinks ?? [])
      .filter((link) => link.customer_id === customerId)
      .map((link) => buildOptionValue(link.property_id, link.property_unit_id ?? "")),
  )

  const close = () => {
    if (submitting) return

    onremoveOverlay?.()
  }

  const submit = async () => {
    if (submitting) return

    const targets = Array.from(
      new Set((selectedUnits ?? []).map((entry) => entry.trim()).filter(Boolean)),
    ).filter((entry) => !linkedTargets.includes(entry))

    if (!targets.length) {
      newNotification({
        class: "error",
        html: "Bitte mindestens eine noch nicht zugeordnete Einheit auswählen.",
      })

      return
    }

    submitting = true
    try {
      const created: PropertyCustomer[] = []
      for (const target of targets) {
        const [propertyId, unitIdRaw] = target.split("::")
        const propertyUnitId = unitIdRaw?.trim()

        if (!propertyId || !propertyUnitId) continue

        const normalizedRole = normalizePropertyCustomerRole(role)
        const link = await createPropertyCustomer({
          property_id: propertyId,
          property_unit_id: propertyUnitId,
          customer_id: customerId,
          role: normalizedRole,
          outreach_enabled: defaultOutreachEnabledForRole(normalizedRole),
          is_primary: false,
        })
        created.push(link)
      }

      if (!created.length) {
        newNotification({ class: "error", html: "Es konnte keine Einheit zugeordnet werden." })

        return
      }

      const knownPropertyIds = new Set((properties ?? []).map((property) => property.id))
      const missingPropertyIds = Array.from(
        new Set(created.map((link) => link.property_id).filter((id) => !knownPropertyIds.has(id))),
      )
      const loadedProperties = missingPropertyIds.length
        ? await fetchProperties({
            organizationId,
            propertyIds: missingPropertyIds,
            limit: missingPropertyIds.length,
          })
        : []
      onLinked?.({ links: created, properties: [...properties, ...(loadedProperties ?? [])] })
      newNotification({ class: "success", html: "Einheiten wurden zugeordnet." })
      onremoveOverlay?.()
    } catch (err) {
      console.error("customer_unit_selection_failed", err)
      newNotification({ class: "error", html: "Einheiten konnten nicht zugeordnet werden." })
    } finally {
      submitting = false
    }
  }

  onMount(() => {
    onshowOverlay?.()
    setActionButtonSnippet?.(actionButtons)
  })

  onDestroy(() => {
    setActionButtonSnippet?.(null)
  })
</script>

<div class="unit-selection-overlay form light">
  <PropertyUnitAsyncMultiSelect
    {organizationId}
    seedProperties={properties}
    excludeValues={linkedTargets}
    placeholder="Einheiten auswählen"
    bind:value={selectedUnits}
    brightBackground={true}
    disabled={submitting}
  />

  <Input
    type="select"
    placeholder="Rolle für diese Einheit"
    options={propertyCustomerRoleOptions}
    bind:value={role}
    brightBackground={true}
    disabled={submitting}
  />
</div>

{#snippet actionButtons()}
  <CTA type="secondaryButton" content="Abbrechen" onClick={close} disabled={submitting} />
  <CTA
    type="primaryButton"
    content={submitting ? "Ordne zu…" : "Zuordnen"}
    onClick={submit}
    disabled={submitting}
  >
    {#snippet iconSnippet()}
      <Icon path={mdiSelectionEllipseRemove} />
    {/snippet}
  </CTA>
{/snippet}

<style lang="less">
  .unit-selection-overlay {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 1.2rem;

    :global(label),
    :global(.fieldShell),
    :global(.select-input-shell) {
      width: 100%;
      max-width: 100%;
    }
  }
</style>
